/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'clsx';
import { useFormik } from 'formik';
import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useLocation } from 'react-router';
import * as Yup from 'yup';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';

import { HUModal2 } from '../../../../components/HUModal2/HUModal2';
import { MarkdownEditor } from '../../../../components/MarkdownEditor/MarkdownEditor';
import { SimpleErrorModal } from '../../../../components/SimpleErrorModal/SimpleErrorModal';
import { ArrowRoundedIcon } from '../../../../components/svg-icons/ArrowRoundedIcon';
import { SelectCluster } from '../../../../containers/CreateClusterOverlay/components/SelectCluster/SelectCluster';
import { formFromBackupInitialValues } from '../../../../containers/CreateClusterOverlay/CreateClusterOverlay.consts';
import { CreateClusterFromBackupValues } from '../../../../containers/CreateClusterOverlay/CreateClusterOverlay.types';
import { HUAutocomplete } from '../../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../../elements/HUTextInput/utils';
import { Namespaces } from '../../../../i18n';
import { SupportJiraApiService } from '../../../../services/SupportJiraApiService';
import { useFormStyles } from '../../../../styles/form';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { IUser } from '../../../../types/user/IUser';
import { fetchJiraRequestTypeFields } from '../../../../utils/queryFns';
import { useApiValidation } from '../../../../utils/useApiValidation';
import { useValidationTranslation } from '../../../../utils/useValidationTranslation';
import { useJiraSupportStyles } from '../styles';
import { getLabelKey } from '../utils';
import { JiraFileUpload } from './JiraFileUpload';

interface Props {
  requestType: {
    id: string;
    name: string;
  };
  setRequestTypeId: (value: { id: string; name: string } | null) => void;
  user?: IUser;
  onSuccess: () => void;
  onClose: () => void;
}

const schemaTypeMap = {
  string: 'text',
  number: 'number',
  datetime: 'datetime-local',
  attachment: 'file',
  priority: 'select',
  option: 'select',
};

const arrayItemMap = {
  attachment: 'file',
  string: 'autocomplete',
  component: 'autocomplete',
  version: 'autocomplete',
};

export const CreateTicket = memo<Props>(({ requestType, setRequestTypeId, onSuccess, onClose }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [errorModal, setErrorModal] = useState<string | null>();

  const formClasses = useFormStyles();
  const classes = useJiraSupportStyles();
  const { t } = useTranslation([Namespaces.Common, Namespaces.General]);
  const { vtr } = useValidationTranslation();

  const { setApiError, getFieldApiError } = useApiValidation();

  const clusterSelectionFormik = useFormik<CreateClusterFromBackupValues>({
    onSubmit: () => {},
    initialValues: {
      ...formFromBackupInitialValues,
      backupClusterId: queryParams.get('cluster') ?? undefined,
      backupProjectId: queryParams.get('project') ?? undefined,
      backupOrganizationId: queryParams.get('organization') ?? undefined,
    },
  });

  const [createJiraSupportTicket, { isLoading: isCreateTicketLoading }] = useMutation(
    SupportJiraApiService.createCustomerRequest,
    {
      onSuccess,
      onError: (error: ApiClientError) => handleError(error),
    },
  );

  const onFormSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      await createJiraSupportTicket({
        data: {
          clusterId: clusterSelectionFormik.values.backupCluster?.id,
          requestTypeId: requestType.id ?? '',
          requestFieldValues: values,
        },
      });
    },
    [clusterSelectionFormik.values, createJiraSupportTicket, requestType],
  );

  const {
    data: requestTypeFields,
    isLoading: isLoadingTypeFields,
    isError: isErrorTypeFields,
  } = useQuery([fetchJiraRequestTypeFields.key, { id: requestType.id }], fetchJiraRequestTypeFields.fn, {
    enabled: true,
  });

  const { values, touched, errors, handleBlur, handleChange, handleSubmit, submitForm, dirty, setFieldValue } =
    useFormik<Record<string, unknown>>({
      validationSchema: Yup.object({
        ...requestTypeFields?.payload.reduce((acc, cur) => {
          const base =
            cur.schema.type === 'array'
              ? {
                  attachment: Yup.mixed,
                  string: () => Yup.array().of(Yup.string()),
                }[cur.schema.items]
              : {
                  number: Yup.number,
                  string: Yup.string,
                  datetime: Yup.date,
                  priority: () => Yup.object({ id: Yup.string().oneOf(cur.validValues.map(v => v.value)) }),
                }[cur.schema.type];

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error - `base` can be a Yup schema constructor or a function returning a schema
          let validation = base ? base() : Yup.string();

          if (cur.required) {
            validation = validation.required('empty');
          }

          return { ...acc, [cur.fieldId]: validation };
        }, {}),
      }),
      enableReinitialize: true,
      initialValues:
        requestTypeFields?.payload.reduce(
          (acc, cur) => ({
            ...acc,
            [cur.fieldId]:
              cur.schema.type === 'priority' && cur.schema.system === 'priority'
                ? { id: cur.defaultValues[0]?.value ?? '' }
                : (cur.defaultValues[0]?.value ?? ''),
          }),
          {},
        ) || {},
      onSubmit: onFormSubmit,
    });

  const handleError = (error: ApiClientError) => {
    const validation = setApiError(error);
    if (validation.errorType === 'unexpected' || validation.errorType === 'common') {
      setErrorModal(t(`${Namespaces.Common}:errors.unexpected_error`));
    }
  };

  const getError = (field: string) => errors[field] || getFieldApiError(field) || '';

  const hasError = (field: string) => !!touched[field] && !!getError(field);

  const handleCloseError = () => setErrorModal(null);

  return (
    <HUModal2
      open
      size="large"
      onOk={submitForm}
      onCancel={onClose}
      disableRestoreFocus
      onClose={onClose}
      title={t(getLabelKey('createAnIssue', true))}
      cancelButtonText={t(getLabelKey('cancel', true))}
      okButtonText={t(getLabelKey('createIssue', true))}
      okButtonProps={{
        disabled: !dirty,
        startIcon: isCreateTicketLoading ? <CircularProgress color="secondary" size={20} /> : null,
      }}
    >
      <Box style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <Button onClick={() => setRequestTypeId(null)} variant="outlined">
            Back
          </Button>
          <Typography
            style={{
              fontSize: '16px',
              lineHeight: '24px',
              fontWeight: 'bold',
              color: '#1F3F6D',
            }}
          >
            {requestType.name}
          </Typography>
        </Box>
        <Box>
          {isLoadingTypeFields ? (
            <Box style={{ display: 'flex', justifyContent: 'center' }}>
              <Typography>{t(getLabelKey('fetchingRequestTypeFields'))}</Typography>
            </Box>
          ) : isErrorTypeFields ? (
            <Box style={{ display: 'flex', justifyContent: 'center' }}>
              <Alert severity="error">{t(getLabelKey('errorFetchingRequestTypeFields'))}</Alert>
            </Box>
          ) : !requestTypeFields || !requestTypeFields.payload.length ? (
            <Box style={{ display: 'flex', justifyContent: 'center' }}>
              <Typography>{t(getLabelKey('noRequestTypeFields'))}</Typography>
            </Box>
          ) : (
            <Box>
              <form noValidate onSubmit={handleSubmit}>
                {requestTypeFields?.payload.map(field => (
                  <FormControl
                    key={field.fieldId}
                    className={cx(formClasses.formControl, formClasses.widthFull)}
                    variant="outlined"
                    error={hasError(field.fieldId)}
                    required={field.required}
                  >
                    <InputLabel shrink={false} htmlFor={field.fieldId} className={classes.inputLabel}>
                      {field.name}
                    </InputLabel>
                    {field.schema.type === 'string' && field.schema.system === 'description' ? (
                      <MarkdownEditor
                        value={String(values[field.fieldId] ?? '')}
                        onChange={handleChange(field.fieldId)}
                      />
                    ) : field.schema.type === 'array' && arrayItemMap[field.schema.items] ? (
                      arrayItemMap[field.schema.items] === 'autocomplete' ? (
                        <HUAutocomplete
                          id={field.fieldId}
                          {...(field.validValues.length
                            ? {
                                options: field.validValues.map(value => value.label),
                              }
                            : { freeSolo: true, options: [] })}
                          freeSolo
                          multiple
                          value={values[field.fieldId] as unknown[]}
                          onChange={handleChange(field.fieldId)}
                          popupIcon={<ArrowRoundedIcon />}
                          renderInput={renderParams => <OutlinedInput {...destructRenderInputParams(renderParams)} />}
                        />
                      ) : field.schema.items === 'attachment' ? (
                        <JiraFileUpload
                          id={field.fieldId}
                          name={field.name}
                          handleChange={attachmentIds => {
                            void setFieldValue(field.fieldId, attachmentIds);
                          }}
                        />
                      ) : (
                        <OutlinedInput />
                      )
                    ) : field.schema.type !== 'array' && schemaTypeMap[field.schema.type] ? (
                      schemaTypeMap[field.schema.type] === 'select' ? (
                        <Select
                          id={field.fieldId}
                          defaultValue={field.defaultValues[0]?.value}
                          value={values[field.fieldId]}
                          onChange={event => handleChange(event)}
                        >
                          {field.validValues.map(value => (
                            <MenuItem key={value.label} value={value.value}>
                              {value.label}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <OutlinedInput
                          id={field.fieldId}
                          name={field.name}
                          type={schemaTypeMap[field.schema.type]}
                          error={hasError(field.fieldId)}
                          value={values[field.fieldId]}
                          onChange={handleChange(field.fieldId)}
                          onBlur={handleBlur(field.fieldId)}
                          fullWidth
                        />
                      )
                    ) : (
                      <Alert severity="error">
                        It looks like JIRAs{' '}
                        <i>
                          {field.schema.type}
                          {field.schema.type === 'array' ? ` of ${field.schema.items}` : ''}
                        </i>{' '}
                        input type has slipped through our QA and has not been implemented.
                        <br />
                        We kindly ask you to report this to CYBERTEC so we may patch this as soon as possible.
                      </Alert>
                    )}

                    {hasError(field.fieldId) && (
                      <FormHelperText>
                        {vtr(getError(field.fieldId), {
                          field: field.name,
                        })}
                      </FormHelperText>
                    )}
                  </FormControl>
                ))}

                <Box className={formClasses.hiddenSubmitBtn}>
                  <Button type="submit" color="primary" variant="contained" size="medium" disabled={dirty}>
                    &nbsp;
                  </Button>
                </Box>

                <Divider orientation="horizontal" variant="fullWidth" style={{ margin: '8px' }} />

                <Box>
                  <Typography style={{ marginBottom: '8px' }}>
                    Do you want to open the ticket for a specific cluster? Doing so will make the ticket visible for
                    every user that has permission to view the cluster.
                  </Typography>
                  <Box>
                    <FormControl
                      className={cx(formClasses.formControl, formClasses.widthMedium)}
                      error={hasError('backupCluster')}
                    >
                      <SelectCluster
                        {...clusterSelectionFormik}
                        titleOverride={t(getLabelKey(`labels.selectCluster`))}
                        purpose="simple"
                      />
                      {hasError('backupCluster') && <FormHelperText>{getError('backupCluster')}</FormHelperText>}
                    </FormControl>
                  </Box>
                </Box>

                <SimpleErrorModal open={!!errorModal?.length} message={errorModal ?? ''} onOk={handleCloseError} />
              </form>
            </Box>
          )}
        </Box>
      </Box>
    </HUModal2>
  );
});
